import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/brand";
import { NAV } from "../lib/testIds";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import SignInRequiredDialog from "./SignInRequiredDialog";
import { fetchMyQuestions } from "../lib/api";

const LINKS = [
    { to: "/how-it-works", label: "How It Works", testId: NAV.howItWorks },
    { to: "/browse", label: "Browse", testId: NAV.browse },
    { to: "/list", label: "List Item", testId: NAV.listItem, requiresAuth: true, action: "list an item" },
    { to: "/dashboard/rentals", label: "Borrowing", testId: NAV.myRentals, requiresAuth: true, action: "open Borrowing" },
    { to: "/dashboard/listings", label: "Lending", testId: NAV.myListings, requiresAuth: true, action: "open Lending" },
    { to: "/messages", label: "Messages", testId: NAV.messages, authOnly: true, showUnread: true },
];

// Routes where the top navigation links are intentionally hidden — the auth
// pages should be a focused sign-in/sign-up experience without the noise of
// dashboard shortcuts the visitor can't use yet.
const AUTH_ROUTES = ["/login", "/signup", "/forgot-password", "/reset-password"];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const { isAuthenticated, logout, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthPage = AUTH_ROUTES.some((p) => location.pathname.startsWith(p));

    // Poll the messages inbox for unread count so the nav badge stays fresh.
    // 30s cadence — cheap on the backend and responsive enough for a chat UX.
    const [messagesUnread, setMessagesUnread] = useState(0);
    useEffect(() => {
        if (!isAuthenticated) {
            setMessagesUnread(0);
            return undefined;
        }
        let cancelled = false;
        const load = () => {
            fetchMyQuestions()
                .then((data) => {
                    if (!cancelled) setMessagesUnread(data?.total_unread || 0);
                })
                .catch(() => {
                    // Silent — the badge just won't update. Not worth toasting.
                });
        };
        load();
        const timer = setInterval(load, 30000);
        // Also refresh on window focus so returning to the tab shows fresh count.
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => {
            cancelled = true;
            clearInterval(timer);
            window.removeEventListener("focus", onFocus);
        };
    }, [isAuthenticated, location.pathname]);

    const visibleLinks = LINKS.filter((l) => !l.authOnly || isAuthenticated);

    // Sign-in nudge dialog — shown when a guest clicks a nav link that needs
    // an account (List Item / Borrowing / Lending) so they get a
    // friendly explanation instead of a silent redirect to /login.
    const [signInPrompt, setSignInPrompt] = useState({ open: false, action: "continue" });
    const guardAuth = (link, e) => {
        if (!isAuthenticated && link.requiresAuth) {
            e.preventDefault();
            setOpen(false);
            setSignInPrompt({ open: true, action: link.action || "continue" });
        }
    };

    const handleSignOut = async () => {
        await logout();
        navigate("/");
    };

    return (
        <header
            data-testid={NAV.root}
            className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#F4F1EA]/85 border-b border-[#2e3220]/10"
        >
            <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-10 md:py-4">
                <Link
                    to="/"
                    data-testid={NAV.logo}
                    className="group flex items-center gap-3"
                    aria-label={`${BRAND.name} home`}
                >
                    <img
                        src={LOGO_URL}
                        alt={`${BRAND.name} logo`}
                        className="h-11 w-11 rounded-xl object-cover shadow-sm transition-transform duration-300 group-hover:rotate-[-4deg]"
                    />
                    <span className="font-display text-2xl font-bold tracking-tight text-[#2e3220]">
                        {BRAND.name}
                    </span>
                </Link>

                {/* Desktop links — hidden on auth pages for a focused experience */}
                {!isAuthPage && (
                    <div className="hidden items-center gap-1 md:flex">
                        {visibleLinks.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={l.testId}
                                onClick={(e) => guardAuth(l, e)}
                                className={({ isActive }) =>
                                    `relative rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                                        isActive
                                            ? "bg-[#E8E2D0] text-[#737a3d]"
                                            : "text-[#2e3220]/85 hover:bg-[#E8E2D0]/70 hover:text-[#737a3d]"
                                    }`
                                }
                            >
                                {l.label}
                                {l.showUnread && messagesUnread > 0 && (
                                    <span
                                        data-testid={`${l.testId}-badge`}
                                        className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#B56C5A] px-1 text-[9px] font-bold text-[#F4F1EA]"
                                    >
                                        {messagesUnread > 9 ? "9+" : messagesUnread}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </div>
                )}

                <div className="hidden items-center gap-2 md:flex">
                    {isAuthenticated ? (
                        <>
                            <NotificationBell />
                            <Link
                                to="/profile"
                                data-testid="nav-profile-link"
                                className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold text-[#5a6142] transition-colors hover:bg-[#E8E2D0]/70 hover:text-[#737a3d]"
                            >
                                {user?.picture ? (
                                    <img
                                        src={user.picture}
                                        alt=""
                                        className="h-7 w-7 rounded-full object-cover"
                                    />
                                ) : (
                                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#737a3d] text-[10px] font-bold text-[#F4F1EA]">
                                        {(user?.name || "N").charAt(0).toUpperCase()}
                                    </span>
                                )}
                                Hi, {user?.name?.split(" ")[0] || "neighbor"}
                            </Link>
                            <button
                                type="button"
                                onClick={handleSignOut}
                                className="btn-ghost"
                                data-testid="nav-logout-btn"
                            >
                                Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className="btn-ghost"
                                data-testid={NAV.login}
                                aria-label="Log in"
                            >
                                <LogIn className="h-4 w-4" strokeWidth={2.5} />
                                Log in
                            </Link>
                            <Link
                                to="/signup"
                                className="btn-primary !py-2 !px-5 text-sm"
                                data-testid={NAV.signup}
                            >
                                Join Lendly
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile toggle (bell + greeting on mobile) — replaced by
                    inline Log in / Join buttons on auth pages for a focused
                    sign-in experience. */}
                {isAuthPage ? (
                    <div className="flex items-center gap-2 md:hidden">
                        <Link
                            to="/login"
                            className="btn-ghost !py-1.5 !px-3 text-xs"
                            data-testid={`${NAV.login}-mobile-inline`}
                            aria-label="Log in"
                        >
                            <LogIn className="h-3.5 w-3.5" strokeWidth={2.5} />
                            Log in
                        </Link>
                        <Link
                            to="/signup"
                            className="btn-primary !py-1.5 !px-3 text-xs"
                            data-testid={`${NAV.signup}-mobile-inline`}
                        >
                            Join
                        </Link>
                    </div>
                ) : (
                    <div className="flex items-center gap-1 md:hidden">
                        {isAuthenticated && <NotificationBell />}
                        {isAuthenticated && (
                            <Link
                                to="/profile"
                                data-testid="nav-profile-greeting-mobile"
                                className="max-w-[110px] truncate rounded-full px-2 py-1 text-xs font-semibold text-[#5a6142] hover:text-[#737a3d]"
                            >
                                Hi, {user?.name?.split(" ")[0] || "neighbor"}
                            </Link>
                        )}
                        <button
                            type="button"
                            className="rounded-full p-2 text-[#2e3220] hover:bg-[#E8E2D0]"
                            onClick={() => setOpen((v) => !v)}
                            data-testid={NAV.mobileToggle}
                            aria-label="Toggle menu"
                            aria-expanded={open}
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                )}
            </nav>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid={NAV.mobileMenu}
                    className="border-t border-[#2e3220]/10 bg-[#F4F1EA] px-5 py-4 md:hidden"
                >
                    <div className="flex flex-col gap-1">
                        {isAuthenticated && (
                            <NavLink
                                to="/profile"
                                data-testid="nav-profile-link-mobile"
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 rounded-xl px-4 py-3 text-base font-semibold ${
                                        isActive
                                            ? "bg-[#E8E2D0] text-[#737a3d]"
                                            : "text-[#2e3220] hover:bg-[#E8E2D0]"
                                    }`
                                }
                            >
                                {user?.picture ? (
                                    <img
                                        src={user.picture}
                                        alt=""
                                        className="h-7 w-7 rounded-full object-cover"
                                    />
                                ) : (
                                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#737a3d] text-[10px] font-bold text-[#F4F1EA]">
                                        {(user?.name || "N").charAt(0).toUpperCase()}
                                    </span>
                                )}
                                My Profile
                            </NavLink>
                        )}
                        {visibleLinks.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`${l.testId}-mobile`}
                                onClick={(e) => {
                                    guardAuth(l, e);
                                    if (isAuthenticated || !l.requiresAuth) setOpen(false);
                                }}
                                className={({ isActive }) =>
                                    `flex items-center justify-between rounded-xl px-4 py-3 text-base font-semibold ${
                                        isActive
                                            ? "bg-[#E8E2D0] text-[#737a3d]"
                                            : "text-[#2e3220] hover:bg-[#E8E2D0]"
                                    }`
                                }
                            >
                                <span>{l.label}</span>
                                {l.showUnread && messagesUnread > 0 && (
                                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B56C5A] px-1.5 text-[10px] font-bold text-[#F4F1EA]">
                                        {messagesUnread > 9 ? "9+" : messagesUnread}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                        <div className="mt-3 flex gap-2">
                            {isAuthenticated ? (
                                <button
                                    type="button"
                                    onClick={handleSignOut}
                                    className="btn-secondary flex-1 !py-2 text-sm"
                                    data-testid={`${NAV.login}-mobile`}
                                >
                                    Sign out
                                </button>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        onClick={() => setOpen(false)}
                                        className="btn-secondary flex-1 !py-2 text-sm text-center"
                                        data-testid={`${NAV.login}-mobile`}
                                    >
                                        Log in
                                    </Link>
                                    <Link
                                        to="/signup"
                                        onClick={() => setOpen(false)}
                                        className="btn-primary flex-1 !py-2 text-sm text-center"
                                        data-testid={`${NAV.signup}-mobile`}
                                    >
                                        Join
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <SignInRequiredDialog
                open={signInPrompt.open}
                onOpenChange={(v) => setSignInPrompt((s) => ({ ...s, open: v }))}
                action={signInPrompt.action}
            />
        </header>
    );
};

export default Navbar;
